import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import ElasticService from "src/services/elasticService";
import SemverService from "src/services/semverService/semverService";
import { makeStyles } from "tss-react/mui";

import { Theme, Typography } from "@mui/material";
import { FilterBadge } from "@shared/CustomBadge";
import { PrimaryButton } from "@shared/CustomButton";
import { CheckboxField } from "@shared/form/CheckboxField";
import { Modal } from "@shared/Modal/Modal";

type FilterOption = { key: string; label: string };
type FilterValues = Record<string, boolean>;

const useStyles = makeStyles()((theme: Theme) => ({
    content: {
        margin: theme.spacing(3, 0),
        background: theme.palette.emteria.background.paper,
        borderRadius: "6px",
    },
    checkboxContainer: {
        display: "flex",
        flexDirection: "column",
        marginLeft: theme.spacing(4),
        padding: theme.spacing(3, 0),
        maxHeight: 300,
        overflow: "auto",
        overflowX: "hidden",
        position: "relative",
    },
    selectManyContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    sortButton: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
    },
    sortText: {
        marginRight: theme.spacing(1),
    },
    sortIcon: {
        marginLeft: theme.spacing(2),
        fontSize: "12px",
        cursor: "pointer",
        color: theme.palette.emteria.link,
    },
}));

const setAllTo = (options: FilterOption[], value: boolean): FilterValues =>
    options.reduce((acc, opt) => ({ ...acc, [opt.key]: value }), {});

const countActive = (values: FilterValues): number => Object.values(values).filter(Boolean).length;

export type Props = {
    title: string;
    options: FilterOption[];
    initialValues: FilterValues;
    onSubmit: (values: FilterValues) => void;
    badgeContent?: number;
    sortDisabled?: boolean;
    defaultSortOrder?: "asc" | "desc";
};

export const FilterHeader = ({ defaultSortOrder = "asc", ...props }: Props) => {
    const [open, setOpen] = React.useState(false);
    const [draft, setDraft] = React.useState<FilterValues>(props.initialValues);
    const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">(defaultSortOrder);

    const location = useLocation();
    const { t } = useTranslation(["common"]);
    const { classes } = useStyles();

    const sortedOptions = props.sortDisabled
        ? props.options
        : [...props.options].sort((a, b) => {
              const result = SemverService.compare(a.key, b.key);
              return sortOrder === "asc" ? result : -result;
          });
    const activeCount = props.badgeContent ?? countActive(props.initialValues);
    const allChecked = props.options.length > 0 && props.options.every((o) => draft[o.key]);

    const handleOpen = () => {
        setDraft(props.initialValues);
        setOpen(true);
        ElasticService.createTransaction("tableFilter", { page: location.pathname, filterType: props.title });
    };

    const handleSave = () => {
        props.onSubmit(draft);
        setOpen(false);
    };

    const handleToggle = (key: string, checked: boolean) => {
        setDraft((prev) => ({ ...prev, [key]: checked }));
    };

    const toggleSort = () => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));

    return (
        <React.Fragment>
            <FilterBadge badgeContent={activeCount} onClick={handleOpen} />
            <Modal
                open={open}
                title={props.title}
                onClose={() => setOpen(false)}
                endButton={<PrimaryButton onClick={handleSave}>{t("common:button.save")}</PrimaryButton>}
            >
                {!props.sortDisabled ? (
                    <div className={classes.sortButton} onClick={toggleSort}>
                        <Typography className={classes.sortText}>{t("common:button.sort")}</Typography>
                        <i className={`fa-solid ${sortOrder === "asc" ? "fa-arrow-up" : "fa-arrow-down"} ${classes.sortIcon}`} />
                    </div>
                ) : null}

                <div className={classes.content}>
                    <div className={classes.checkboxContainer}>
                        {sortedOptions.map((opt) => (
                            <CheckboxField
                                key={opt.key}
                                label={opt.label}
                                checked={!!draft[opt.key]}
                                onChange={(_e, checked) => handleToggle(opt.key, checked)}
                            />
                        ))}
                    </div>
                </div>

                {props.options.length > 0 ? (
                    <div className={classes.selectManyContainer}>
                        <CheckboxField
                            label={t("common:button.selectAll")}
                            checked={allChecked}
                            indeterminate={false}
                            onChange={(_e, checked) => setDraft(setAllTo(props.options, checked))}
                        />
                    </div>
                ) : null}
            </Modal>
        </React.Fragment>
    );
};
